@let comps = comparableCompanies$ | async;
@let data = comparableCompaniesData$ | async;
@let isEditable = isEditable$ | async;
@let isLoading = isLoading$ | async;
@let assetId = assetId$ | async;
@let isEditDisabled = isEditDisabled$ | async;
@let lastRefreshed = lastRefreshed$ | async;
@let isLowConfidence = isLowConfidence$ | async;

<mat-card class="detail-card" valumizeCardContextMenu [commentSlug]="'asset-comparable-companies'">
    <mat-card-header class="detail-card-header">
        <mat-card-title data-test="title">Comparable Companies</mat-card-title>
    </mat-card-header>

    <mat-card-subtitle>
        @if (comps?.status === 'ERROR') {
            <div class="error-message">{{ comps?.errorMessage }}</div>
        }
        @if (comps?.status === 'SAVED') {
            <div class="saved-message">Successfully saved</div>
        }
    </mat-card-subtitle>

    @if (isLowConfidence) {
        <div class="low-confidence-banner" data-test="low-confidence-banner">
            <mat-icon class="low-confidence-icon">info_outline</mat-icon>
            <span>Fewer than 3 comparable companies could be matched to a real ticker. Add a company website or description for better results.</span>
            <button mat-button color="primary" (click)="scrollToEnrichmentFields()">Add details</button>
        </div>
    }

    @if (isLoading) {
        <mat-card-content class="detail-card-content">
            <div class="spinner-container">
                <mat-spinner diameter="36"></mat-spinner>
                <p class="loading-hint">This may take up to a minute...</p>
            </div>
        </mat-card-content>
    }

    @if (!isLoading && data) {
        <mat-card-content class="detail-card-content">
            @if (data.companies && data.companies.length > 0) {
                <div class="table-container">
                    <table mat-table [dataSource]="data.companies" class="comps-table">
                        <ng-container matColumnDef="companyName">
                            <th mat-header-cell *matHeaderCellDef>Company</th>
                            <td mat-cell *matCellDef="let comp">
                                @if (comp.profileUrl) {
                                    <a [href]="comp.profileUrl" target="_blank" class="company-link">{{ comp.companyName }}</a>
                                } @else {
                                    {{ comp.companyName }}
                                }
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="ticker">
                            <th mat-header-cell *matHeaderCellDef>Ticker</th>
                            <td mat-cell *matCellDef="let comp">{{ comp.ticker }}</td>
                        </ng-container>

                        <ng-container matColumnDef="currencyIso">
                            <th mat-header-cell *matHeaderCellDef>Currency</th>
                            <td mat-cell *matCellDef="let comp">{{ comp.currencyIso }}</td>
                        </ng-container>

                        <ng-container matColumnDef="industry">
                            <th mat-header-cell *matHeaderCellDef>Industry</th>
                            <td mat-cell *matCellDef="let comp">{{ comp.industry }}</td>
                        </ng-container>

                        <ng-container matColumnDef="revenue">
                            <th mat-header-cell *matHeaderCellDef class="align-right">Revenue</th>
                            <td mat-cell *matCellDef="let comp" class="align-right">
                                @if (comp.revenue !== null) {
                                    {{ comp.revenue | decimalFormat: "currency" }}
                                }
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="revenueGrowth">
                            <th mat-header-cell *matHeaderCellDef class="align-right">Rev. Growth</th>
                            <td mat-cell *matCellDef="let comp" class="align-right">
                                @if (comp.revenueGrowth !== null) {
                                    {{ comp.revenueGrowth | decimalFormat: "percent" }}
                                }
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="ebitdaMargin">
                            <th mat-header-cell *matHeaderCellDef class="align-right">EBITDA Margin</th>
                            <td mat-cell *matCellDef="let comp" class="align-right">
                                @if (comp.ebitdaMargin !== null) {
                                    {{ comp.ebitdaMargin | decimalFormat: "percent" }}
                                }
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="enterpriseValue">
                            <th mat-header-cell *matHeaderCellDef class="align-right"
                                matTooltip="Enterprise Value (approximate — net debt estimated from total liabilities minus cash)">EV *</th>
                            <td mat-cell *matCellDef="let comp" class="align-right">
                                @if (comp.enterpriseValue !== null) {
                                    {{ comp.enterpriseValue | decimalFormat: "currency" }}
                                }
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="evEbitda">
                            <th mat-header-cell *matHeaderCellDef class="align-right"
                                matTooltip="Enterprise Value / EBITDA (approximate — net debt estimated from total liabilities minus cash)" style="white-space: nowrap">EV/EBITDA&nbsp;*</th>
                            <td mat-cell *matCellDef="let comp" class="align-right">
                                @if (comp.evEbitda !== null) {
                                    {{ comp.evEbitda | decimalFormat: "multiple" }}
                                }
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="marketCap">
                            <th mat-header-cell *matHeaderCellDef class="align-right">Market Cap</th>
                            <td mat-cell *matCellDef="let comp" class="align-right">
                                @if (comp.marketCap !== null) {
                                    {{ comp.marketCap | decimalFormat: "currency" }}
                                }
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="exchange">
                            <th mat-header-cell *matHeaderCellDef>Exchange</th>
                            <td mat-cell *matCellDef="let comp">{{ comp.exchange }}</td>
                        </ng-container>

                        <ng-container matColumnDef="analystNote">
                            <th mat-header-cell *matHeaderCellDef>Note</th>
                            <td mat-cell *matCellDef="let comp">
                                @if (isEditable && comp.id && assetId) {
                                    <input class="note-input"
                                           [value]="comp.analystNote ?? ''"
                                           (blur)="updateNoteFromEvent(assetId, comp.id, $event)"
                                           placeholder="Add note...">
                                } @else {
                                    {{ comp.analystNote }}
                                }
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="actions">
                            <th mat-header-cell *matHeaderCellDef></th>
                            <td mat-cell *matCellDef="let comp">
                                @if (isEditable && comp.id && assetId) {
                                    <button mat-icon-button color="warn"
                                            (click)="removeComp(assetId, comp.id)"
                                            matTooltip="Remove comparable">
                                        <mat-icon>delete</mat-icon>
                                    </button>
                                }
                            </td>
                        </ng-container>

                        <tr mat-header-row *matHeaderRowDef="isEditable ? displayedColumnsWithActions : displayedColumns"></tr>
                        <tr mat-row *matRowDef="let row; columns: isEditable ? displayedColumnsWithActions : displayedColumns;"></tr>
                    </table>
                </div>
                @if (isEditable && assetId) {
                    <div class="add-comp-row">
                        <mat-form-field class="add-comp-field add-comp-name">
                            <mat-label>Company Name</mat-label>
                            <input matInput [(ngModel)]="newCompanyName" placeholder="e.g. Microsoft">
                        </mat-form-field>
                        <mat-form-field class="add-comp-field">
                            <mat-label>Ticker</mat-label>
                            <input matInput [(ngModel)]="newTicker" placeholder="e.g. MSFT"
                                   (keyup.enter)="addComp(assetId, data.companies)">
                        </mat-form-field>
                        <button mat-icon-button color="primary"
                                [disabled]="!newTicker.trim()"
                                (click)="addComp(assetId, data.companies)"
                                matTooltip="Add comparable company">
                            <mat-icon>add_circle</mat-icon>
                        </button>
                    </div>
                }
            } @else {
                <div class="no-content">
                    No comparable companies yet. Use "Suggest Comps" to get started.
                    @if (isEditable && assetId) {
                        <div class="add-comp-row">
                            <mat-form-field class="add-comp-field add-comp-name">
                                <mat-label>Company Name</mat-label>
                                <input matInput [(ngModel)]="newCompanyName" placeholder="e.g. Microsoft">
                            </mat-form-field>
                            <mat-form-field class="add-comp-field">
                                <mat-label>Ticker</mat-label>
                                <input matInput [(ngModel)]="newTicker" placeholder="e.g. MSFT"
                                       (keyup.enter)="addComp(assetId, [])">
                            </mat-form-field>
                            <button mat-icon-button color="primary"
                                    [disabled]="!newTicker.trim()"
                                    (click)="addComp(assetId, [])"
                                    matTooltip="Add comparable company">
                                <mat-icon>add_circle</mat-icon>
                            </button>
                        </div>
                    }
                </div>
            }

            @if (data.summary || data.impliedTrading) {
                <div class="analysis-container">
                    @if (data.summary; as summary) {
                        <div class="summary-section">
                            <div class="summary-columns">
                                <div class="summary-column">
                                    <h4>Valuation multiples</h4>
                                    <div class="summary-item">
                                        <label>Median EV / EBITDA</label>
                                        <span class="summary-value">{{ summary.medianEvEbitda !== null ? (summary.medianEvEbitda | decimalFormat: "multiple") : '-' }}</span>
                                    </div>
                                    <div class="summary-item">
                                        <label>Mean EV / EBITDA</label>
                                        <span class="summary-value">{{ summary.meanEvEbitda !== null ? (summary.meanEvEbitda | decimalFormat: "multiple") : '-' }}</span>
                                    </div>
                                    <div class="summary-item">
                                        <label>Median EV / Revenue</label>
                                        <span class="summary-value">{{ summary.medianEvRevenue !== null ? (summary.medianEvRevenue | decimalFormat: "multiple") : '-' }}</span>
                                    </div>
                                    <div class="summary-item">
                                        <label>Mean EV / Revenue</label>
                                        <span class="summary-value">{{ summary.meanEvRevenue !== null ? (summary.meanEvRevenue | decimalFormat: "multiple") : '-' }}</span>
                                    </div>
                                </div>
                                <div class="summary-column">
                                    <h4>Profitability & growth</h4>
                                    <div class="summary-item">
                                        <label>Median EBITDA margin</label>
                                        <span class="summary-value">{{ summary.medianEbitdaMargin !== null ? (summary.medianEbitdaMargin | decimalFormat: "percent") : '-' }}</span>
                                    </div>
                                    <div class="summary-item">
                                        <label>Mean EBITDA margin</label>
                                        <span class="summary-value">{{ summary.meanEbitdaMargin !== null ? (summary.meanEbitdaMargin | decimalFormat: "percent") : '-' }}</span>
                                    </div>
                                    <div class="summary-item">
                                        <label>Median rev. growth</label>
                                        <span class="summary-value">{{ summary.medianRevenueGrowth !== null ? (summary.medianRevenueGrowth | decimalFormat: "percent") : '-' }}</span>
                                    </div>
                                    <div class="summary-item">
                                        <label>Mean rev. growth</label>
                                        <span class="summary-value">{{ summary.meanRevenueGrowth !== null ? (summary.meanRevenueGrowth | decimalFormat: "percent") : '-' }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }

                    @if (data.impliedTrading; as it) {
                        <div class="implied-trading-section">
                            <h4>GP implied valuation</h4>
                            <table class="implied-trading-table">
                                <tbody>
                                    <tr>
                                        <td class="it-label">NAV{{ it.navCurrency ? ' (' + it.navCurrency + ')' : '' }}</td>
                                        <td class="it-value">{{ it.nav !== null ? (it.nav | decimalFormat: "currency") : '-' }}</td>
                                    </tr>
                                    <tr>
                                        <td class="it-label">Ownership %</td>
                                        <td class="it-value">{{ it.ownershipPercent !== null ? (it.ownershipPercent | decimalFormat: "percent") : '-' }}</td>
                                    </tr>
                                    <tr>
                                        <td class="it-label">Total Equity Value{{ it.navCurrency ? ' (' + it.navCurrency + ')' : '' }}</td>
                                        <td class="it-value">{{ it.totalEquityValue !== null ? (it.totalEquityValue | decimalFormat: "currency") : '-' }}</td>
                                    </tr>
                                    @if (it.fxRate !== null) {
                                        <tr>
                                            <td class="it-label">FX Rate ({{ it.navCurrency }} → {{ it.assetCurrency }})</td>
                                            <td class="it-value">{{ it.fxRate | number:'1.4-4' }}</td>
                                        </tr>
                                        <tr>
                                            <td class="it-label">Total Equity Value ({{ it.assetCurrency }})</td>
                                            <td class="it-value">{{ it.totalEquityValueAssetCurrency !== null ? (it.totalEquityValueAssetCurrency | decimalFormat: "currency") : '-' }}</td>
                                        </tr>
                                    }
                                    <tr>
                                        <td class="it-label">Net Debt{{ it.assetCurrency ? ' (' + it.assetCurrency + ')' : '' }}</td>
                                        <td class="it-value">{{ it.netDebt !== null ? (it.netDebt | decimalFormat: "currency") : '-' }}</td>
                                    </tr>
                                    <tr class="it-highlight-row">
                                        <td class="it-label">GP Implied EV{{ it.assetCurrency ? ' (' + it.assetCurrency + ')' : '' }}</td>
                                        <td class="it-value">{{ it.gpImpliedEv !== null ? (it.gpImpliedEv | decimalFormat: "currency") : '-' }}</td>
                                    </tr>
                                    <tr class="it-separator">
                                        <td colspan="2"></td>
                                    </tr>
                                    <tr>
                                        <td class="it-label">EBITDA{{ it.ebitdaBasis ? ' (' + it.ebitdaBasis + ')' : '' }}</td>
                                        <td class="it-value">{{ it.ebitda !== null ? (it.ebitda | decimalFormat: "currency") : '-' }}</td>
                                    </tr>
                                    <tr>
                                        <td class="it-label">Revenue{{ it.ebitdaBasis ? ' (' + it.ebitdaBasis + ')' : '' }}</td>
                                        <td class="it-value">{{ it.revenue !== null ? (it.revenue | decimalFormat: "currency") : '-' }}</td>
                                    </tr>
                                    <tr class="it-highlight-row">
                                        <td class="it-label">GP EV / EBITDA</td>
                                        <td class="it-value">{{ it.gpEvEbitda !== null ? (it.gpEvEbitda | decimalFormat: "multiple") : '-' }}</td>
                                    </tr>
                                    <tr class="it-highlight-row">
                                        <td class="it-label">GP EV / Revenue</td>
                                        <td class="it-value">{{ it.gpEvRevenue !== null ? (it.gpEvRevenue | decimalFormat: "multiple") : '-' }}</td>
                                    </tr>
                                    @if (it.gpDiscountToMedian !== null && it.gpDiscountToMedian !== undefined) {
                                        <tr>
                                            <td class="it-label">
                                                GP vs. Comp Median
                                                <mat-icon class="info-icon"
                                                          matTooltip="GP implied EV/EBITDA versus the median of the comparable companies. Red = the GP holds the asset above comparable public valuations (a premium — typically unfavourable); green = below the comps (a discount).">info_outline</mat-icon>
                                            </td>
                                            <td class="it-value" [class.negative]="it.gpDiscountToMedian! > 0" [class.positive]="it.gpDiscountToMedian! < 0">
                                                {{ it.gpDiscountToMedian | decimalFormat: "percent" }}
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                            @if (it.missingInputs?.length) {
                                <div class="missing-inputs">
                                    <mat-icon class="missing-icon">info_outline</mat-icon>
                                    <span>Some values could not be calculated: {{ it.missingInputs!.join(', ') }}</span>
                                </div>
                            }
                        </div>
                    }
                </div>
            }
        </mat-card-content>
    }

    @if (!isLoading && !data) {
        <mat-card-content class="detail-card-content">
            <div class="no-content">No comparable companies data available.</div>
        </mat-card-content>
    }

    <mat-card-actions class="detail-card-actions">
        @if (lastRefreshed) {
            <span class="last-refreshed">Last refreshed: {{ lastRefreshed | date:'dd/MM/yyyy HH:mm' }}</span>
        }
        @if (isEditable && assetId) {
            <button mat-icon-button
                    (click)="dispatchSuggest()"
                    matTooltip="AI-powered comparable company suggestions"
                    class="image-button"
                    aria-label="Suggest Comps">
                <img src="assets/logo.svg" alt="Valumize AI" />
            </button>
            <button mat-icon-button
                    (click)="dispatchRefresh(data)"
                    [disabled]="!data?.companies?.length"
                    matTooltip="Refresh market data"
                    color="primary">
                <mat-icon>refresh</mat-icon>
            </button>
        }
        <span class="spacer"></span>
        @if (!isEditable) {
            <button mat-icon-button
                    color="primary"
                    data-test="edit-button"
                    (click)="edit()"
                    [disabled]="isEditDisabled!">
                <mat-icon>edit</mat-icon>
            </button>
        }
        @if (isEditable && assetId && data) {
            <button mat-icon-button color="primary" data-test="save-button" (click)="save(assetId, data.companies)">
                <mat-icon>save</mat-icon>
            </button>
        }
        @if (isEditable) {
            <button mat-icon-button color="primary" data-test="cancel-button" (click)="cancel()">
                <mat-icon>cancel</mat-icon>
            </button>
        }
        <valumize-card-comments-button [commentSlug]="'asset-comparable-companies'"></valumize-card-comments-button>
    </mat-card-actions>
</mat-card>
